import { Popover } from "@chakra-ui/react"
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"

type AugmentationDialogRegistration = {
  close?: () => void
}

type AugmentationDialogContextValue = {
  hasOpenDialog: boolean
  registerDialog: (registration?: AugmentationDialogRegistration) => () => void
}

const AugmentationDialogContext =
  createContext<AugmentationDialogContextValue | null>(null)

export function useAugmentationDialogs(): AugmentationDialogContextValue {
  const [openDialogCount, setOpenDialogCount] = useState(0)
  const dialogStackRef = useRef<AugmentationDialogRegistration[]>([])

  const registerDialog = useCallback((registration = {}) => {
    let active = true
    dialogStackRef.current = [...dialogStackRef.current, registration]
    setOpenDialogCount((count) => count + 1)

    return () => {
      if (!active) return

      active = false
      dialogStackRef.current = dialogStackRef.current.filter(
        (dialog) => dialog !== registration
      )
      setOpenDialogCount((count) => Math.max(0, count - 1))
    }
  }, [])

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return

      const topDialog = dialogStackRef.current[dialogStackRef.current.length - 1]
      if (!topDialog?.close) return

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      topDialog.close()
    }

    document.addEventListener("keydown", handleKeyDown, true)

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true)
    }
  }, [])

  return useMemo(
    () => ({
      hasOpenDialog: openDialogCount > 0,
      registerDialog
    }),
    [openDialogCount, registerDialog]
  )
}

export function AugmentationDialogProvider({
  dialogs,
  children
}: {
  dialogs: AugmentationDialogContextValue
  children: React.ReactNode
}) {
  return (
    <AugmentationDialogContext.Provider value={dialogs}>
      {children}
    </AugmentationDialogContext.Provider>
  )
}

export function useAugmentationDialog(
  active: boolean,
  registration?: AugmentationDialogRegistration
) {
  const dialogs = useContext(AugmentationDialogContext)
  const registerDialog = dialogs?.registerDialog

  useEffect(() => {
    if (!active) return

    return registerDialog?.(registration)
  }, [active, registerDialog, registration])
}

export function AugmentationPopover({
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: Popover.RootProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(!!defaultOpen)
  const isOpen = open ?? uncontrolledOpen

  const closePopover = useCallback(() => {
    if (open === undefined) setUncontrolledOpen(false)
    onOpenChange?.({ open: false })
  }, [onOpenChange, open])

  const registration = useMemo(
    () => ({
      close: closePopover
    }),
    [closePopover]
  )

  useAugmentationDialog(isOpen, registration)

  return (
    <Popover.Root
      {...props}
      open={isOpen}
      // We disable default escape behavior
      // because we want to handle nested dialogs,
      // which seem to be glitchy with Chakra's default behavior.
      closeOnEscape={false}
      onOpenChange={(details) => {
        if (open === undefined) setUncontrolledOpen(details.open)
        onOpenChange?.(details)
      }}
    />
  )
}
